import { Directive, forwardRef, HostBinding, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yRole } from '../interfaces/a11y.interfaces';
import { normalizeAriaValue } from '../utils/a11y.util';

import { A11yCarouselContext, A11yCarouselDotContext } from './a11y-carousel.context';
import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11yCarousel]',
  providers: [
    { provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yCarouselDirective) },
    { provide: A11yCarouselContext, useExisting: forwardRef(() => A11yCarouselDirective) }
  ]
})
export class A11yCarouselDirective extends A11yLabelDirective implements A11yRole, A11yCarouselContext {
  private readonly _translationService = inject(TranslationService);
  private readonly _dots: A11yCarouselDotContext[] = [];

  @Input() public khA11yCarouselRoleDescription: string | null = null;

  private _defaultLabel = 'Carousel';
  private _defaultRoleDescription = 'Carousel';

  @HostBinding('attr.role') public readonly role = 'region';

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._defaultLabel = this._translationService.instant('A11Y_CAROUSEL.CAROUSEL') || this._defaultLabel;
        this._defaultRoleDescription = this._translationService.instant('A11Y_CAROUSEL.CAROUSEL') || this._defaultRoleDescription;
      });
  }

  public registerDot(dot: A11yCarouselDotContext): void {
    if (!this._dots.includes(dot)) {
      this._dots.push(dot);
    }
  }

  public unregisterDot(dot: A11yCarouselDotContext): void {
    const index = this._dots.indexOf(dot);

    if (index >= 0) {
      this._dots.splice(index, 1);
    }
  }

  @HostBinding('attr.aria-roledescription')
  public get ariaRoleDescription(): string {
    return normalizeAriaValue(this.khA11yCarouselRoleDescription) ?? this._defaultRoleDescription;
  }

  protected override get ariaLabelValue(): string | null {
    return normalizeAriaValue(super.ariaLabelValue) ?? this._defaultLabel;
  }
}
